/**
 * The graph controller: owns the canvas, the simulation handle, the camera,
 * the render loop, and all pointer input.
 *
 * Deliberately a plain class rather than a pile of effects. The render loop,
 * the smoothing buffers and the sim handle all have to stay in lockstep across
 * topology swaps, which is very hard to keep correct spread across hooks.
 */
import { MainSim, createSim } from "./sim";
import type { SimHandle, SimResponse } from "./sim/protocol";
import type { ForceOpts } from "./sim/forces";
import { GraphRenderer, type RenderDisplay, type RenderScene } from "./renderer";
import { resolvePalette, type GraphPalette } from "./palette";
import { carryOver, positionsFor, writeBack } from "./positions";
import { Playback } from "./timeline";
import {
  boundsOf,
  clampZoom,
  easeCamera,
  fitCamera,
  makeCamera,
  toWorld,
  wheelDelta,
  type Camera,
} from "./camera";
import type { GraphData, GraphNode } from "./types";
import type { GraphScope } from "@/store/graph";

/** Exponential smoothing time constant. */
const TAU_MS = 42;
/** Cap DPR at 2: 3x is 2.25x the fill cost for no visible gain on 4px dots. */
const MAX_DPR = 2;
const CLICK_SLOP_PX = 4;
const CLICK_MS = 400;

export interface ControllerCallbacks {
  onOpen: (node: GraphNode, newTab: boolean) => void;
  onHover: (node: GraphNode | null) => void;
  onPlaybackChange: () => void;
}

export class GraphController {
  readonly playback = new Playback();
  private renderer = new GraphRenderer();
  private sim: SimHandle | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private host: HTMLElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private ro: ResizeObserver | null = null;
  private dprCleanup: (() => void) | null = null;
  private raf = 0;

  private data: GraphData | null = null;
  private linkA = new Int32Array(0);
  private linkB = new Int32Array(0);
  private degreeFinal = new Int32Array(0);
  private degree = new Int32Array(0);
  private lastActiveLinks = 0;
  private disp = new Float32Array(0);
  private target = new Float32Array(0);
  private enter = new Float32Array(0);
  private highlight = new Uint8Array(0);

  private cam: Camera = makeCamera();
  private epoch = 0;
  private cssW = 0;
  private cssH = 0;
  private dpr = 1;
  private palette: GraphPalette = resolvePalette();
  private display: RenderDisplay = {
    showArrows: false,
    colorByFolder: true,
    textFadeMultiplier: 0,
    nodeSizeMultiplier: 1,
    lineSizeMultiplier: 1,
  };
  private forces: ForceOpts = {
    centerStrength: 0.5,
    repelStrength: 10,
    linkStrength: 1,
    linkDistance: 250,
    nodeSizeMultiplier: 1,
    nodeCount: 0,
  };

  private hoverIndex = -1;
  /** Eased 0..1 hover emphasis; the renderer dims everything else by it. */
  private dim = 0;
  private currentIndex = -1;
  private dragIndex = -1;
  private dragMoved = false;
  private pointerDownAt = 0;
  private pointerDownPos = { x: 0, y: 0 };
  private panning = false;
  private pendingDrag: { x: number; y: number } | null = null;
  /**
   * The camera tracks the layout until it settles or the user takes over.
   * Fitting only on the first frame does not work: at that point positions are
   * still the seed cluster, so the graph then expands straight off screen.
   */
  private autoFit = true;
  /** First fit snaps; later ones glide, so growth does not lurch. */
  private camPlaced = false;
  private lastFrameAt = 0;

  constructor(
    private readonly scope: GraphScope,
    private readonly cb: ControllerCallbacks,
  ) {}

  // ---------- lifecycle ----------

  attach(canvas: HTMLCanvasElement, host: HTMLElement): void {
    this.canvas = canvas;
    this.host = host;
    this.ctx = canvas.getContext("2d");
    this.palette = resolvePalette();

    // The canvas box changes for reasons the panel-width CSS vars cannot
    // capture: rail collapse, the sign-in banner, focus mode, the mobile
    // drawer. One observer is correct where deriving from vars is a permanent
    // source of "one banner too tall" bugs.
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(host);
    this.dprCleanup = watchDpr(() => this.resize());
    this.resize();

    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointermove", this.onPointerMove);
    canvas.addEventListener("pointerup", this.onPointerUp);
    canvas.addEventListener("pointerleave", this.onPointerLeave);
    canvas.addEventListener("dblclick", this.onDoubleClick);

    this.loop(performance.now());
  }

  detach(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.ro?.disconnect();
    this.ro = null;
    this.dprCleanup?.();
    this.dprCleanup = null;
    const c = this.canvas;
    if (c) {
      c.removeEventListener("wheel", this.onWheel);
      c.removeEventListener("pointerdown", this.onPointerDown);
      c.removeEventListener("pointermove", this.onPointerMove);
      c.removeEventListener("pointerup", this.onPointerUp);
      c.removeEventListener("pointerleave", this.onPointerLeave);
      c.removeEventListener("dblclick", this.onDoubleClick);
    }
    this.sim?.detach();
    this.sim = null;
    this.canvas = null;
    this.host = null;
    this.ctx = null;
  }

  /** The topology the playhead indexes into. */
  get topology(): GraphData | null {
    return this.data;
  }

  // ---------- data ----------

  setDisplay(display: RenderDisplay): void {
    this.display = display;
    this.forces = { ...this.forces, nodeSizeMultiplier: display.nodeSizeMultiplier };
  }

  setForces(f: Omit<ForceOpts, "nodeCount" | "nodeSizeMultiplier">): void {
    this.forces = {
      ...f,
      nodeSizeMultiplier: this.display.nodeSizeMultiplier,
      nodeCount: this.data?.nodes.length ?? 0,
    };
    // display-only settings must never reach here: they are read per frame
    this.sim?.send({ type: "forces", epoch: this.epoch, forces: this.forces });
  }

  setCurrentItem(itemId: string | null): void {
    const id = itemId ? `n:${itemId}` : null;
    this.currentIndex = id ? (this.data?.byId.get(id)?.index ?? -1) : -1;
  }

  /** Swap topology. Positions carry over by node id, so layout survives. */
  setData(data: GraphData): void {
    const prev = this.data;
    this.data = data;
    const n = data.nodes.length;
    const m = data.links.length;

    if (prev && this.disp.length === prev.nodes.length * 2) {
      writeBack(this.scope, prev.nodes, this.disp);
    }
    carryOver(this.scope, data.nodes, data.adjacency, this.forces.linkDistance);

    this.linkA = new Int32Array(m);
    this.linkB = new Int32Array(m);
    for (let j = 0; j < m; j++) {
      const l = data.links[j]!;
      this.linkA[j] = data.byId.get(l.source)?.index ?? 0;
      this.linkB[j] = data.byId.get(l.target)?.index ?? 0;
    }

    this.degreeFinal = new Int32Array(n);
    for (let i = 0; i < n; i++) this.degreeFinal[i] = data.nodes[i]!.degree;
    this.degree = new Int32Array(n);
    this.lastActiveLinks = 0;

    const reg = positionsFor(this.scope);
    const pos = new Float32Array(n * 2);
    for (let i = 0; i < n; i++) {
      const p = reg.get(data.nodes[i]!.id);
      pos[i * 2] = p?.x ?? 0;
      pos[i * 2 + 1] = p?.y ?? 0;
    }

    this.disp = pos.slice();
    this.target = pos.slice();
    this.enter = new Float32Array(n).fill(1);
    this.highlight = new Uint8Array(n);
    this.hoverIndex = -1;
    this.dragIndex = -1;

    this.playback.setGraph(data, this.palette.enterMs);
    this.forces = { ...this.forces, nodeCount: n };

    const cold = !prev || prev.nodes.length === 0;
    const wantWorker = n > 250;
    const haveWorker = this.sim !== null && !(this.sim instanceof MainSim);
    if (!this.sim || wantWorker !== haveWorker) {
      this.sim?.detach();
      this.sim = createSim(n);
      this.sim.onMessage(this.onSimMessage);
    }
    this.epoch++;

    const activeNodes = this.playback.isAnimating ? Math.floor(this.playback.cursor) : n;
    const activeLinks = this.playback.activeLinks(activeNodes);
    this.syncDegree(activeLinks);

    this.sim.send(
      {
        type: "setGraph",
        epoch: this.epoch,
        nodeCount: n,
        pos,
        degree: this.degreeFinal.slice(),
        linkSource: this.linkA.slice(),
        linkTarget: this.linkB.slice(),
        activeNodes,
        activeLinks,
        cold,
        forces: this.forces,
      },
      [],
    );
    if (cold) {
      this.autoFit = true;
      this.camPlaced = false;
    }
  }

  // ---------- playback ----------

  togglePlayback(): void {
    const wasIdle = !this.playback.isAnimating;
    this.playback.toggle(performance.now());
    // Watching it build is the point, so the camera follows the growth. Any
    // deliberate pan or zoom hands control straight back to the user.
    if (this.playback.state === "playing") this.autoFit = true;
    if (wasIdle) this.pushActive(true);
    this.cb.onPlaybackChange();
  }

  /** Begin creation playback unless it is already running. */
  startPlayback(): void {
    if (this.playback.state === "playing") return;
    this.togglePlayback();
  }

  resetPlayback(): void {
    this.playback.reset();
    this.pushActive(true);
    this.autoFit = true;
    this.cb.onPlaybackChange();
  }

  seekPlayback(fraction: number): void {
    this.playback.seek(fraction, performance.now());
    this.pushActive(true);
    this.cb.onPlaybackChange();
  }

  /** Frame the live layout. Uses the sim's latest, not the smoothed copy. */
  zoomToFit(smoothing = 0): void {
    if (!this.data) return;
    const want = fitCamera(boundsOf(this.target, this.playbackActiveNodes()), this.cssW, this.cssH);
    this.cam = smoothing > 0 && this.camPlaced ? easeCamera(this.cam, want, smoothing) : want;
    this.camPlaced = true;
  }

  /** Any deliberate camera or node input hands control to the user. */
  private takeOverCamera(): void {
    this.autoFit = false;
  }

  // ---------- internals ----------

  private playbackActiveNodes(): number {
    const n = this.data?.nodes.length ?? 0;
    return this.playback.isAnimating ? Math.min(n, Math.floor(this.playback.cursor)) : n;
  }

  private onSimMessage = (msg: SimResponse): void => {
    if (msg.type === "settled") {
      if (msg.epoch === this.epoch && this.autoFit) {
        this.zoomToFit();
        this.autoFit = false;
      }
      return;
    }
    if (msg.epoch !== this.epoch) {
      // stale topology: dropping this is what stops a 2*n_old write into a
      // 2*n_new array the first time a note is created while the graph is open
      return;
    }
    const incoming = new Float32Array(msg.pos);
    if (incoming.length === this.target.length) this.target.set(incoming);
    if (this.sim && !(this.sim instanceof MainSim)) {
      this.sim.send({ type: "recycle", buffer: msg.pos }, [msg.pos]);
    }
  };

  private syncDegree(activeLinks: number): void {
    if (!this.data) return;
    if (activeLinks < this.lastActiveLinks) {
      this.degree.fill(0);
      this.lastActiveLinks = 0;
    }
    for (let j = this.lastActiveLinks; j < activeLinks; j++) {
      const a = this.linkA[j]!;
      const b = this.linkB[j]!;
      this.degree[a] = (this.degree[a] ?? 0) + 1;
      this.degree[b] = (this.degree[b] ?? 0) + 1;
    }
    this.lastActiveLinks = activeLinks;
  }

  private pushActive(force: boolean): void {
    if (!this.sim || !this.data) return;
    const activeNodes = this.playbackActiveNodes();
    const activeLinks = this.playback.isAnimating
      ? this.playback.activeLinks(activeNodes)
      : this.data.links.length;
    this.syncDegree(activeLinks);
    if (!force && activeLinks === this.lastActiveLinks && !this.playback.isAnimating) return;
    this.sim.send({
      type: "active",
      epoch: this.epoch,
      activeNodes,
      activeLinks,
      warm: this.playback.state === "playing",
    });
  }

  private resize(): void {
    const host = this.host;
    const canvas = this.canvas;
    if (!host || !canvas) return;
    const rect = host.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width));
    const h = Math.max(1, Math.round(rect.height));
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    if (w === this.cssW && h === this.cssH && dpr === this.dpr) return;
    this.cssW = w;
    this.cssH = h;
    this.dpr = dpr;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    this.ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private loop = (now: number): void => {
    this.raf = requestAnimationFrame(this.loop);
    const ctx = this.ctx;
    const data = this.data;
    if (!ctx || !data) return;

    const dt = this.lastFrameAt ? Math.min(100, now - this.lastFrameAt) : 16;
    this.lastFrameAt = now;

    // main-thread sim ticks inside the render frame, so there is no second
    // timer competing with rAF
    if (this.sim instanceof MainSim) this.sim.pump(4);

    if (this.playback.isAnimating) {
      const before = this.playbackActiveNodes();
      this.playback.advance(now);
      if (this.playbackActiveNodes() !== before) this.pushActive(false);
    }

    if (this.pendingDrag && this.dragIndex >= 0) {
      // coalesced to one message per frame: pointermove fires at up to 500Hz
      // on a high-poll mouse and flooding the queue makes drag feel sticky
      this.sim?.send({ type: "drag", epoch: this.epoch, index: this.dragIndex, ...this.pendingDrag });
      this.pendingDrag = null;
    }

    const s = 1 - Math.exp(-dt / TAU_MS);
    for (let i = 0; i < this.disp.length; i++) {
      this.disp[i] = this.disp[i]! + (this.target[i]! - this.disp[i]!) * s;
    }

    // hover dim washes in and out rather than snapping; on hover end the last
    // highlight set is kept (see setHover) so the fade-out keeps its shape
    const dimTarget = this.hoverIndex >= 0 ? 1 : 0;
    this.dim += (dimTarget - this.dim) * (1 - Math.exp(-dt / 110));
    if (this.dim < 0.005) this.dim = 0;

    const activeNodes = this.playbackActiveNodes();
    this.playback.fillEnter(this.enter, activeNodes, now);

    if (this.autoFit && activeNodes > 0 && this.cssW > 0) {
      // glide while the layout (or the playback) is still growing
      this.zoomToFit(1 - Math.exp(-dt / 180));
    }

    const scene: RenderScene = {
      data,
      camera: this.cam,
      linkA: this.linkA,
      linkB: this.linkB,
      disp: this.disp,
      enter: this.enter,
      degree: this.playback.isAnimating ? this.degree : this.degreeFinal,
      activeNodes,
      activeLinks: this.playback.isAnimating ? this.playback.activeLinks(activeNodes) : data.links.length,
      hoverIndex: this.hoverIndex,
      dimStrength: this.dim,
      highlight: this.highlight,
      currentIndex: this.currentIndex,
      palette: this.palette,
      display: this.display,
    };
    this.renderer.draw(ctx, this.cssW, this.cssH, scene);
    this.scene = scene;
  };

  private scene: RenderScene | null = null;

  // ---------- input ----------

  private localPoint(e: PointerEvent | WheelEvent | MouseEvent): { x: number; y: number } {
    const rect = this.canvas?.getBoundingClientRect();
    return { x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) };
  }

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.takeOverCamera();
    const p = this.localPoint(e);
    const dy = wheelDelta(e, this.cssH);
    const factor = Math.exp(-dy * 0.0015);
    this.cam = zoomAtLocal(this.cam, p.x, p.y, factor, this.cssW, this.cssH);
  };

  private onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0 && e.button !== 1) return;
    const p = this.localPoint(e);
    this.takeOverCamera();
    this.pointerDownAt = performance.now();
    this.pointerDownPos = p;
    this.dragMoved = false;
    this.canvas?.setPointerCapture(e.pointerId);
    const hit = this.scene ? this.renderer.hitTest(this.scene, p.x, p.y) : -1;
    if (hit >= 0) {
      this.dragIndex = hit;
      this.panning = false;
    } else {
      this.dragIndex = -1;
      this.panning = true;
    }
  };

  private onPointerMove = (e: PointerEvent): void => {
    const p = this.localPoint(e);
    if (this.dragIndex >= 0) {
      const w = toWorld(p.x, p.y, this.cam, this.cssW, this.cssH);
      this.pendingDrag = { x: w.x, y: w.y };
      this.dragMoved = true;
      return;
    }
    if (this.panning) {
      const dx = p.x - this.pointerDownPos.x;
      const dy = p.y - this.pointerDownPos.y;
      if (Math.abs(dx) > CLICK_SLOP_PX || Math.abs(dy) > CLICK_SLOP_PX) this.dragMoved = true;
      this.cam = { ...this.cam, x: this.cam.x - dx / this.cam.z, y: this.cam.y - dy / this.cam.z };
      this.pointerDownPos = p;
      return;
    }
    this.updateHover(p.x, p.y);
  };

  private onPointerUp = (e: PointerEvent): void => {
    const p = this.localPoint(e);
    const wasDrag = this.dragIndex;
    const quick = performance.now() - this.pointerDownAt < CLICK_MS;
    if (this.dragIndex >= 0) {
      this.sim?.send({ type: "dragEnd", epoch: this.epoch, index: this.dragIndex });
      this.pendingDrag = null;
    }
    this.dragIndex = -1;
    this.panning = false;
    this.canvas?.releasePointerCapture(e.pointerId);
    if (wasDrag >= 0 && !this.dragMoved && quick && this.data) {
      const node = this.data.nodes[wasDrag];
      if (node) this.cb.onOpen(node, e.ctrlKey || e.metaKey || e.button === 1);
    }
    this.updateHover(p.x, p.y);
  };

  private onPointerLeave = (): void => {
    this.setHover(-1);
  };

  private onDoubleClick = (): void => {
    this.zoomToFit();
    this.autoFit = false;
  };

  private updateHover(px: number, py: number): void {
    const hit = this.scene ? this.renderer.hitTest(this.scene, px, py) : -1;
    if (hit !== this.hoverIndex) this.setHover(hit);
  }

  private setHover(index: number): void {
    this.hoverIndex = index;
    const data = this.data;
    // on hover END the highlight set is deliberately NOT cleared: the dim is
    // still fading out and needs the old emphasis shape to fade from
    if (index >= 0 && data) {
      this.highlight.fill(0);
      const node = data.nodes[index];
      if (node) {
        this.highlight[index] = 1;
        const a = data.adjacency.get(node.id);
        if (a) {
          for (const id of a.out) {
            const i = data.byId.get(id)?.index;
            if (i !== undefined) this.highlight[i] = 1;
          }
          for (const id of a.in) {
            const i = data.byId.get(id)?.index;
            if (i !== undefined) this.highlight[i] = 1;
          }
        }
      }
    }
    if (this.canvas) this.canvas.style.cursor = index >= 0 ? "pointer" : "grab";
    this.cb.onHover(index >= 0 ? (this.data?.nodes[index] ?? null) : null);
  }
}

function zoomAtLocal(cam: Camera, sx: number, sy: number, factor: number, w: number, h: number): Camera {
  const before = toWorld(sx, sy, cam, w, h);
  const z = clampZoom(cam.z * factor);
  return { z, x: before.x - (sx - w / 2) / z, y: before.y - (sy - h / 2) / z };
}

/**
 * DPR changes (browser zoom, dragging between monitors) at constant CSS size,
 * which ResizeObserver cannot see. window.resize misses monitor changes and
 * fires for irrelevant reasons, so both are needed.
 */
function watchDpr(onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof matchMedia !== "function") return () => {};
  let mq: MediaQueryList | null = null;
  const fire = () => {
    arm();
    onChange();
  };
  const arm = () => {
    mq?.removeEventListener("change", fire);
    mq = matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    mq.addEventListener("change", fire);
  };
  arm();
  return () => mq?.removeEventListener("change", fire);
}
